import { useState, useEffect, useMemo } from 'react';
import { Combobox, ComboboxOption } from '@/components/ui/combobox';
import { Skeleton } from '@/components/ui/skeleton';
import { getAssistants } from '@/api/assistants/assistants.api';
import type { Assistant } from '@/api/assistants/assistants.types';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ShieldAlert } from 'lucide-react';

interface AssistantSelectorProps {
    selectedAssistantId: string | undefined;
    onAssistantSelect: (assistantId: string | undefined) => void;
    className?: string;
}

export const AssistantSelector = ({ 
    selectedAssistantId, 
    onAssistantSelect, 
    className 
}: AssistantSelectorProps) => {
    const [assistants, setAssistants] = useState<Assistant[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAssistants = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const apiResponse = await getAssistants();
                if (apiResponse.error) throw new Error(apiResponse.error);
                
                let assistantsData: Assistant[] = [];
                const responseData = apiResponse.data;
                if (responseData) {
                    if (Array.isArray(responseData)) assistantsData = responseData;
                    else if ((responseData as { data?: Assistant[] }).data && Array.isArray((responseData as { data?: Assistant[] }).data)) assistantsData = (responseData as { data: Assistant[] }).data;
                    else if ((responseData as { assistants?: Assistant[] }).assistants && Array.isArray((responseData as { assistants?: Assistant[] }).assistants)) assistantsData = (responseData as { assistants: Assistant[] }).assistants;
                }
                setAssistants(assistantsData);
            } catch (err) {
                 const message = err instanceof Error ? err.message : 'Failed to fetch assistants';
                 setError(message);
                 console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAssistants();
    }, []);

    const options: ComboboxOption[] = useMemo(() => [
        { value: 'all', label: 'All Assistants' },
        ...assistants.map(assistant => ({
            value: assistant.id,
            label: assistant.name,
            description: assistant.id
        }))
    ], [assistants]);

    const handleChange = (value: string | undefined) => {
        onAssistantSelect(value === 'all' ? undefined : value);
    };

    if (isLoading) {
        return <Skeleton className={cn("h-9 w-[250px]", className)} />;
    }

    if (error) {
        // Check if it's a permission error (403)
        const isPermissionError = error.includes('403') || error.toLowerCase().includes('forbidden');
        
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div>
                            <Combobox
                                options={[]}
                                value={undefined}
                                onValueChange={() => {}}
                                placeholder={isPermissionError ? "No access" : "Error loading"}
                                disabled
                                className={cn("w-[250px]", className)}
                            />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent className="flex items-center gap-2 max-w-xs">
                        <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0" />
                        <p className="text-sm">
                            {isPermissionError 
                                ? "You don't have permission to view assistants. Contact your administrator for access."
                                : "Failed to load assistants. Please try refreshing the page."}
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return (
        <Combobox
            options={options}
            value={selectedAssistantId || 'all'}
            onValueChange={handleChange}
            placeholder="All Assistants"
            searchPlaceholder="Search assistant..."
            emptyMessage="No assistant found."
            className={cn("w-[250px]", className)}
        />
    );
};
